/*
sharerNoNotice.js — the Sharer No. warnings, as pure functions (XOL-626 D12, XOL-416).

They used to live in ProjectTab's computeds. The sharer form in RTS step 3 owns the same
input now, so both forms read ONE definition:

    ProjectTab (label 'Sharer #')      the site's Project tab
    LicenseeForm (label 'Sharer No.')  RTS step 3, Create and Modify

The Sharer No. is the sharer's BILLING SLOT. Under a rate billed by Sharer No it picks the
price column, so:
  - 0 means unassigned: the fee cannot be PRICED (the sharer should still be billed);
  - two live sharers on one number bill at the same position, and the Billing Audit
    flags each of them.
Neither blocks a save: a speed bump, never a gate. Strict By-Qty rates take the position
from the live sharer count and never read the number, so every notice is suppressed there
(an UNKNOWN billing mode counts as "the number matters", which errs toward warning).

No gate on who is looking lives in here: callers decide whether to SHOW a notice
(`canEditSharerNo`). `canEdit` below only words the ending of a read-mode note, so the
reader is told who can act.

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

// Every message carries this: these states only affect rates billed by Sharer No, and
// saying so stops a reader assuming their whole billing setup is broken.
export var CAVEAT = 'This only affects rates billed by Sharer No.'

// D11: who may change the Sharer No. The API's own refusal reads
// "Only a finance or project manager, or a super user, can change the Sharer No."
// (xolas-tms-api SharerController, sharer_no_forbidden).
export var SHARER_NO_EDITORS = 'a finance or project manager, or a super user'

export var LABEL_PROJECT = 'Sharer #'
export var LABEL_STEP = 'Sharer No.'

function isStep (label) {
  return label === LABEL_STEP
}

// 'Sharer #3' on the Project tab, 'Sharer No. 3' in step 3.
function ref (label, n) {
  return isStep(label) ? 'Sharer No. ' + n : 'Sharer #' + n
}

// "Every sharer on #3" (Project tab) / "Every sharer numbered 3" (step 3).
function everyOn (label, n) {
  return isStep(label) ? 'Every sharer numbered ' + n : 'Every sharer on #' + n
}

/**
 * The number as an integer, or null when there is none (null, undefined, '', NaN).
 * A numeric string is accepted ('3' -> 3).
 */
export function normaliseSharerNo (v) {
  if (v === null || typeof v === 'undefined' || v === '') return null

  var n = Number(v)

  return (isNaN(n) || !isFinite(n)) ? null : Math.trunc(n)
}

/**
 * Strict By-Qty takes the position from the live sharer count and never reads the
 * number. An unknown mode (not loaded / the fetch failed) counts as "it matters".
 */
export function sharerNoDrivesFee (billingMode) {
  return String(billingMode).toLowerCase() !== 'qty'
}

// Only a Terminated agreement holds no billing slot. An Expired sharer is still a valid
// sharer until it is Terminated (XOL-652 D2, which reverses XOL-626 D32), so its number
// counts as taken. A new RTS for a telco whose earlier sharer is Expired (XOL-636) gets the
// next free number from the API instead of reusing the Expired one.
// The API's rule exactly (xolas-tms-api XOL-652 §9): Terminated, ignoring case and trailing spaces.
function holdsNoSlot (status) {
  return String(status || '').trim().toLowerCase() === 'terminated'
}

/**
 * The other LIVE sharers on the same number. 0 and "no number" collide with nothing (two
 * sharers at 0 hold no slot), a Terminated peer holds no slot (an Expired one still does,
 * XOL-652 D2), and the sharer itself is never its own duplicate.
 */
export function duplicatePeers (n, peers, selfId) {
  var num = normaliseSharerNo(n)

  if (num === null || num === 0) return []

  var self = Number(selfId)

  return (peers || []).filter(function (p) {
    if (!p) return false
    if (selfId !== null && selfId !== undefined && Number(p.id) === self) return false
    if (holdsNoSlot(p.status)) return false

    return Number(p.sharerNo) === num
  })
}

/**
 * "A", "A and B", "A, B and C". A peer with no organisation name reads "sharer {id}".
 */
export function peerNames (peers) {
  var names = (peers || []).map(function (p) {
    return (p.organisation && p.organisation.name) ? p.organisation.name : ('sharer ' + p.id)
  })

  if (names.length <= 1) return names.join('')
  if (names.length === 2) return names[0] + ' and ' + names[1]

  return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
}

// Local escaper (the repo has none): organisation names come from the API and end up in
// a `dangerouslyUseHTMLString` confirm.
export function escapeHtml (s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * The EDIT-time warning, for whoever can change the number: { title, description } for an
 * el-alert, or null. 0 SUBSUMES duplicate (two sharers both at 0 are not a collision).
 *
 * @param {Object} o  { n, peers, selfId, billingMode, label }
 */
export function noticeFor (o) {
  var label = o.label || LABEL_PROJECT

  if (!sharerNoDrivesFee(o.billingMode)) return null

  var n = normaliseSharerNo(o.n)

  if (n === 0) {
    return {
      title: ref(label, 0) + " — unassigned, so the fee can't be priced",
      description: 'This sharer will still be billed, but no amount can be calculated: every period ' +
        'in its fee schedule and fee timeline shows no price, and nothing becomes invoiceable. ' +
        CAVEAT + ' Use 1–99 to give it a billing position.'
    }
  }

  var peers = duplicatePeers(n, o.peers, o.selfId)

  if (peers.length) {
    return {
      title: ref(label, n) + ' is already used by ' + peerNames(peers),
      description: everyOn(label, n) + ' will bill at position #' + n + ' of the rate table. ' +
        CAVEAT + ' Saving is allowed, but the Billing Audit will flag each of them.'
    }
  }

  return null
}

/**
 * The READ-mode note: { lead, detail, peersTooltip } or null. It stands alone above a
 * form, so it names the field AND the value, and each state ends by naming who can act
 * rather than implying the reader should.
 *
 * @param {Object} o  { n, peers, selfId, billingMode, label, canEdit }
 */
export function noteFor (o) {
  var label = o.label || LABEL_PROJECT
  var canEdit = !!o.canEdit

  if (!sharerNoDrivesFee(o.billingMode)) return null

  var n = normaliseSharerNo(o.n)

  if (n === 0) {
    var setIt = canEdit
      ? (isStep(label) ? ' Use Edit to set a number from 1 to 99.' : ' Set a number from 1 to 99 to give it one.')
      : ' Only ' + SHARER_NO_EDITORS + ', can set a number from 1 to 99.'

    return {
      lead: isStep(label)
        ? "Sharer No. is unassigned, so its fee can't be priced"
        : "Sharer # is 0 — unassigned, so its fee can't be priced",
      detail: 'This sharer should still be billed, but with no billing position no fee can be ' +
        'calculated under a rate billed by Sharer No. Every period in its fee schedule and fee ' +
        'timeline shows no price, and nothing becomes invoiceable.' + setIt,
      peersTooltip: null
    }
  }

  var peers = duplicatePeers(n, o.peers, o.selfId)

  if (!peers.length) return null

  if (peers.length === 1) {
    return {
      lead: ref(label, n) + ' is also used by ' + peerNames(peers),
      detail: 'Both sharers bill at position #' + n + ' of any rate billed by Sharer No.' +
        (canEdit
          ? " Change one of them if they shouldn't share a position."
          : ' Only ' + SHARER_NO_EDITORS + ', can change one of them.'),
      peersTooltip: null
    }
  }

  return {
    lead: ref(label, n) + ' is also used by ' + peers.length + ' other sharers on this site',
    detail: 'They all bill at position #' + n + ' of any rate billed by Sharer No.' +
      (canEdit
        ? " Change them if they shouldn't share a position."
        : ' Only ' + SHARER_NO_EDITORS + ', can change them.'),
    peersTooltip: (isStep(label) ? 'Also numbered ' + n : 'Also on #' + n) + ': ' + peerNames(peers)
  }
}

export function confirmTitleFor (label) {
  return 'Check the ' + (label || LABEL_PROJECT) + ' before saving'
}

/**
 * The pre-save confirm body (an HTML string for `dangerouslyUseHTMLString`), or null when
 * there is nothing to confirm. Organisation names are ESCAPED. The confirm is only ever
 * shown to someone who can act, EXCEPT a non-manager creating a sharer in step 3
 * (`creatorCannotEdit`), who is told that a manager must change it later.
 *
 * @param {Object} o  { n, peers, selfId, billingMode, label, creatorCannotEdit }
 */
export function confirmHtmlFor (o) {
  var label = o.label || LABEL_PROJECT

  if (!sharerNoDrivesFee(o.billingMode)) return null

  var n = normaliseSharerNo(o.n)
  var item

  if (n === 0) {
    item = '<li><b>' + ref(label, 0) + ' means unassigned.</b> This sharer should still be billed, but with no ' +
      'billing position its fee cannot be calculated — every period in its fee schedule and fee ' +
      'timeline shows no price, and nothing becomes invoiceable, until it has a ' + label + ' between 1 and 99. ' +
      CAVEAT + '</li>'
  } else {
    var peers = duplicatePeers(n, o.peers, o.selfId)

    if (!peers.length) return null

    var until = o.creatorCannotEdit
      ? 'until ' + SHARER_NO_EDITORS + ', changes one of them. '
      : 'until one of them is changed. '

    item = '<li><b>' + ref(label, n) + ' is already used by ' + escapeHtml(peerNames(peers)) + ' on this site.</b> ' +
      everyOn(label, n) + ' bills at position #' + n + ' of the rate table, and each one is ' +
      'flagged in the Billing Audit ' + until + CAVEAT + '</li>'
  }

  return '<p>Before saving, please note:</p><ul style="margin:0;padding-left:20px;text-align:left;">' + item + '</ul>'
}
